import { useEffect } from 'react';
import { useIntersection } from '@/hooks/useIntersection';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useTranslation } from 'react-i18next';

interface LoadingTriggerProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  error?: Error | null;
  onRetry?: () => void;
}

export const LoadingTrigger = ({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  error,
  onRetry
}: LoadingTriggerProps) => {
  const { t } = useTranslation();
  const { ref, isIntersecting } = useIntersection({ threshold: 200 });

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage && !error) {
      onLoadMore();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, onLoadMore, error]);

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">
          {t('records.loading.trigger.error', { message: error.message })}
        </p>
        <Button onClick={onRetry} variant="outline">
          {t('records.loading.trigger.retry')}
        </Button>
      </div>
    );
  }

  // End of list
  if (!hasNextPage) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('records.loading.trigger.all_loaded')}
      </div>
    );
  }

  // Loading state
  return (
    <div ref={ref} className="py-4">
      {isFetchingNextPage ? (
        <div className="space-y-4">
          <div className="flex justify-center mb-4">
            <LoadingSpinner message={t('records.loading.trigger.loading_more')} />
          </div>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={onLoadMore}
          className="w-full"
        >
          {t('records.loading.trigger.load_more')}
        </Button>
      )}
    </div>
  );
};